import { Component, ElementRef, Inject, OnInit, OnDestroy, ViewChild, HostBinding } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ISubset, IMessage, IQueryResponseUpdate, IQueryResponseToolCompleteUpdate, IQueryResponseToolUsageUpdate } from 'src/app/interfaces/message.interface';
import {
  IQuestion,
  EMBEDDING_MESSAGE_TYPE,
  IClientOptionsForFrontend,
  IAccessConfig,
  IAccessInfo,
  IUserInfoField,
  IDetailsButton,
} from 'src/app/interfaces/embedding-options.interface';
import { AccessControlService } from '../../services/access-control/access-control.service';
import { PopoutService } from '../../services/popout.service';
import { AuthService } from '../../services/auth.service';
import {Title} from "@angular/platform-browser";
import { environment } from 'src/environments/environment';
import { stripWrappingCodeFence } from '@cb-common/utils/markdown.utils';
import {
  buildClaractPublicChatUrl,
  claractEmbedRedirectBlocker,
  conversationIdFromPublicChatPath,
  logClaractEmbedRedirectSkipped,
} from '@cb-common/utils/claract-public-chat-redirect.utils';

const UNFINISHED_MARKDOWN_LINK_REGEX = /((\[[^\]]*)|(\[[^\]]*\][^\(]+)|(\[[^\]]*\]([^\)\s]*)))$/;

/**
 * How long an embedded page waits for an embed.js-style host to post
 * omm_cb_init before falling back to URL-derived init. Hosts post the init on
 * iframe load, i.e. right after the app bootstraps — the grace period keeps the
 * URL fallback from rendering first and being visibly repainted (flash) when
 * the richer host options arrive a beat later.
 */
const OMM_CB_INIT_GRACE_MS = 500;

const ERROR_MAP: { [key: string]: string } = {
  'CONTENT_FILTER': 'Entschuldigung, aber der Inhalt dieser Anfrage wurde von unserem System blockiert. Falls Sie glauben, dass dies ein Fehler ist, nehmen Sie bitte Kontakt mit uns auf.',
  'GENERIC_ERROR': 'Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal.',
  'NO_DATASET': 'Leider wurde das gewünschte Dataset nicht gefunden.',
  'FILE_CONTENT_TOO_LARGE': 'Der angehängte Inhalt ist zu lang für das gewählte Modell. Bitte verkürzen Sie die Anfrage oder hängen Sie ein kleineres Dokument an.',
  // Codes the server has classified since CB-326 but this map never learned.
  // `ERROR_MAP[error] || error` falls through to the RAW CODE, so a rate-limited
  // request rendered the literal string "RATE_LIMIT" as the assistant's answer.
  'FILE_EXTRACTION_FAILED': 'Keine der angehängten Dateien konnte gelesen werden. Bitte prüfen Sie die Dateien und versuchen Sie es erneut.',
  'TIMEOUT': 'Die Anfrage hat zu lange gedauert und wurde abgebrochen. Bitte versuchen Sie es erneut.',
  'RATE_LIMIT': 'Die Server des Modellanbieters sind derzeit ausgelastet und haben diese Anfrage abgewiesen (Anfragelimit erreicht). Dieses Limit liegt beim Anbieter und kann von uns weder erhöht noch umgangen werden. Bitte warten Sie einige Minuten und versuchen Sie es erneut.',
  'MODEL_ERROR': 'Das Sprachmodell konnte diese Anfrage nicht verarbeiten. Bitte versuchen Sie es erneut.',
  'INTERNAL': 'Es ist leider ein interner Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
  // cc-qx52 — connection dropped / went silent mid-answer.
  'STREAM_ABORTED': 'Die Verbindung zum Modell wurde unterbrochen, bevor die Antwort fertig war.',
  'STREAM_STALLED': 'Das Modell hat keine Daten mehr gesendet, bevor die Antwort fertig war.',
  // cc-b1lz — output budget fully consumed by internal reasoning; nothing was emitted.
  'MODEL_NO_OUTPUT': 'Das Modell hat sein gesamtes Antwortbudget für interne Überlegungen verbraucht und keine Antwort erzeugt. Bitte versuchen Sie es erneut oder wählen Sie ein anderes Modell.',
};

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: [
    './chat.component.scss',
    './chat.theme.v2.scss'
  ],
})
export class ChatComponent implements OnInit, OnDestroy {
  @ViewChild('chatEntriesContainer') public chatEntriesContainer:
    | ElementRef
    | undefined;

  @ViewChild('queryInput') public queryInput: ElementRef | undefined;

  @HostBinding('class') get themeClass() {
    return `theme-${this.theme}`;
  }

  private id = '';
  public openContext: any = null;
  public loading = true;
  public botIsTyping = false;
  public messages: IMessage[] = [];
  public lastToolUsage: {
    toolName: string;
    agentName: string;
  } | undefined;
  public inputText = '';
  public isEmbedded = getQueryParam('embedded') === 'true';
  /** True when the page is loaded inside an iframe (regardless of the ?embedded query param). */
  public isInIframe = window.self !== window.top;
  // TODO: Remove legacy 'dataset' query parameter fallback once all embeds are updated to use 'assistant'
  public dataset = getQueryParam('assistant') || getQueryParam('dataset');
  public initialSubset = getQueryParam('subset');
  public detectFollowUp = getQueryParam('detect_fq') === 'false' ? false : true; // default: true
  public subset: ISubset = {
    text: '',
    value: this.initialSubset,
    functions: '',
    persona: getQueryParam('persona') || undefined,
  };
  public subsetWeights = getQueryParam('ssw');
  public initialQuestion?: IQuestion = getQueryParamJson('init');
  public welcomeMessage? = 'Hi, how can I help you?';
  public inputPlaceholder = 'Your question...';
  public sendButtonLabel = 'Send';
  public assistant = {
    name: 'Assistant',
  };
  public sampleQuestions: string[] = [];
  public showNewConversationButton = true;
  public showSourcePills = false;
  /** Whether the "open in new window" button is offered (set from client_config.popout). */
  public popoutEnabled = false;
  public showRating = true;
  public negativeRatingText = '';
  public showEmailOnNegativeRating = true;
  public detailsButtons: IDetailsButton[] = [];
  public stage: 'chat' | 'login'  | 'redirect' | 'blocked' | 'denied' | 'pending' = 'chat';
  public theme: 'v1' | 'v2' = 'v1';

  public queryUserInfo?: IUserInfoField[];

  public loggedIn = false;
  public loggedInUser = '';
  public authPageConfig?: IAccessConfig;

  public pickedFiles: File[] = [];

  private protectedWithLogin = false;
  public publicRegistrationAllowed = false;

  /** Keycloak IDP hint for this dataset — if set, login redirects directly to the external IDP. */
  private kcIdpHint?: string;

  /** Stored init params so we can re-run checkAndInit after Storage Access recovery. */
  private _initOptions: IClientOptionsForFrontend | null = null;
  private _initParentOrigin: string | null = null;

  /**
   * Monotonic token so a stale init run (e.g. the URL-derived init racing a
   * later omm_cb_init) can't apply its result after a newer run started —
   * "last invoked" must win, not "last fetch to resolve".
   */
  private _initSequence = 0;

  /**
   * Pending URL-fallback init timer. Armed ONLY for ?embedded=true with a
   * dataset in the URL and no preloaded omm_cb_init_data — every other init
   * path stays synchronous and undelayed.
   */
  private _urlInitFallbackTimer: ReturnType<typeof setTimeout> | null = null;

  // in case the dataset is "just" an agent-proxy
  public type: IClientOptionsForFrontend['type'] = 'chatbot';

  public get isAgentic(): boolean {
    return this.type === 'agent';
  }

  public get showFilePicker(): boolean {
    return this.isAgentic;
  }

  public get showInitialQuestion(): boolean {
    return !this.initialSubset && this.initialQuestion != null;
  }

  public get initialChoiceRequired(): boolean {
    return this.showInitialQuestion && (!this.subset.value && !this.subset.persona && !this.subset.functions && !this.subset.text);
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private accessControlService: AccessControlService,
    private authService: AuthService,
    private title: Title,
    public popoutService: PopoutService,
  ) {
    this.initChat();
  }

  /** Entry-point dispatch — extracted from the constructor so specs can re-run it with controlled fields. */
  private initChat(): void {
    if (this.popoutService.isPopout) {
      // Popped-out top-level window: authorize via the opener handshake first,
      // then load a fresh chat (legacy v1 can't resume a past conversation).
      this.initPopout();
    } else if (this.isEmbedded && this.document.defaultView) {
      this.loading = true;
      if ((window as any).omm_cb_init_data) {
        const options: IClientOptionsForFrontend = (window as any).omm_cb_init_data.options;
        this.checkAndInit(options);
      } else {
        this.document.defaultView.addEventListener('message', async (event) => {
          if (event.data.type === EMBEDDING_MESSAGE_TYPE.INIT) {
            const options: IClientOptionsForFrontend = event.data.options;
            this.checkAndInit(options, event.origin);
          }
        });
        if (this.dataset) {
          // Plain-iframe embed: the host set ?embedded=true with the dataset in
          // the URL but never posts omm_cb_init. Initialize from the URL after
          // a short grace period — otherwise the chat hangs on the loading
          // spinner forever and a dataset configured for v2 never reaches the
          // redirect in getOptionsWithAccessCheck. The grace period lets an
          // omm_cb_init host win outright (no flash); should one still arrive
          // later, its checkAndInit run supersedes this one via _initSequence.
          const seqAtSchedule = this._initSequence;
          this._urlInitFallbackTimer = setTimeout(() => {
            this._urlInitFallbackTimer = null;
            if (this._initSequence === seqAtSchedule) {
              this.checkAndInit({ ds: this.dataset });
            }
          }, OMM_CB_INIT_GRACE_MS);
        }
      }
      this.document.defaultView.postMessage(EMBEDDING_MESSAGE_TYPE.READY, '*');
    } else {
      const skipServerConfig = getQueryParam('skipServerConfig') === 'true';
      if (!skipServerConfig) {
        this.checkAndInit({
          ds: this.dataset
        });
      } else {
        this.loading = false;
      }
    }
  }

  private async getOptionsWithAccessCheck(options: IClientOptionsForFrontend) {
    try {
      if (!options.ds) {
        this.stage = 'blocked';
        throw new Error('No dataset specified');
      }

      // check what kind of access is required
      const access = await fetch(`/api/v1/datasets/${options.ds}/access`);
      if (!access.ok) {
        this.stage = 'blocked';
        throw new Error('Dateset not found');
      }
      const auth = await access.json() as IAccessInfo;

      // Tenant migrated to Claract (bd ai-hub-d10j.6): replace this page with
      // the Claract public chat for the same assistant. Checked BEFORE the v2
      // hop so a migrated visitor is not bounced twice. Inside an iframe this
      // navigates the iframe itself, so the customer's embed keeps working.
      // A malformed target yields null and the legacy chat continues.
      //
      // Embedded: the host's omm_cb_init cannot follow the iframe to Claract
      // (embed.js posts it to the legacy origin). Redirect only when Claract
      // works without it — no requiredReferrers, no host-supplied client
      // options; otherwise stay on legacy and log (customer must re-embed).
      // Rationale in claractEmbedRedirectBlocker.
      const claractUrl = buildClaractPublicChatUrl(auth.migrated_to, options.ds, {
        migratedDatasetId: auth.migrated_dataset_id,
        conversationId: conversationIdFromPublicChatPath(window.location.pathname),
        embedded: getQueryParam('embedded'),
        widget: getQueryParam('widget'),
      });
      if (claractUrl) {
        const blocker = claractEmbedRedirectBlocker({
          embedded: this.isEmbedded || this.isInIframe || getQueryParam('widget') === 'true',
          hasRequiredReferrers: auth.has_required_referrers,
          initOptionKeys: Object.keys(options),
        });
        if (!blocker) {
          this.navigateAway(claractUrl);
          this.stage = 'redirect';
          return options;
        }
        logClaractEmbedRedirectSkipped(options.ds, blocker);
      }

      // Redirect to v2 public chat if the dataset is configured for the new UI
      if (auth.public_chat_version === 'v2') {
        window.location.href = this.buildV2RedirectUrl(options.ds, getQueryParam('embedded'));
        this.stage = 'redirect';
        return options;
      }

      this.protectedWithLogin = auth.sharing_scope !== 'public';
      this.publicRegistrationAllowed = auth.sharing_scope === 'authenticated' || auth.sharing_scope === 'restricted';
      this.authPageConfig = auth.public_config;
      this.kcIdpHint = auth.kc_idp_hint;

      // make sure the user is logged in if required
      if (this.protectedWithLogin) {
        this.loggedIn = this.authService.isAuthenticated;
        if (!this.loggedIn) {
          if (this.isInIframe) {
            // Show the login page. In iframes we always show the page (instead
            // of auto-redirecting for tenant scope) so the user gesture from
            // clicking "Login" can be used by the Storage Access API / popup.
            this.stage = 'login';
            throw new Error('login required');
          } else {
            this.authService.login(window.location.href, false, this.kcIdpHint);
            this.stage = 'redirect';
            throw new Error('login required');
          }
        } else {
          this.loggedInUser = this.authService.username;
        }
      }

      // verify that the user has access to the dataset
      const config = await fetch(`/api/v1/datasets/${options.ds}/config`);
      if (config.status === 402) {
        this.stage = 'pending';
        throw new Error('Access request pending');
      }
      if (!config.ok) {
        this.stage = 'denied';
        throw new Error('Access denied');
      }
      return config?.json();
    } catch (e) {
      if (this.stage === 'chat') {
        this.stage = 'blocked'
      }
      if (this.authPageConfig?.css) {
        const style = this.document.createElement('style');
        style.appendChild(this.document.createTextNode(this.authPageConfig.css));
        this.document.head.appendChild(style);
      }
    }
  }

  /** Full-page replace; a seam so specs can observe it without leaving the page. */
  private navigateAway(url: string): void {
    window.location.replace(url);
  }

  /**
   * Build the v2 public-chat URL for the legacy→v2 redirect. An explicit
   * ?embedded=true/false flag is carried over so it keeps overriding v2's
   * iframe auto-detection; any other value (or no flag) is omitted so v2
   * auto-detects.
   */

  private buildV2RedirectUrl(ds: string, embeddedParam?: string): string {
    const embeddedSuffix = embeddedParam === 'true' || embeddedParam === 'false'
      ? `&embedded=${embeddedParam}`
      : '';
    const params = `assistant=${encodeURIComponent(ds)}${embeddedSuffix}`;
    // Extract only conversation path segments (e.g. /c/:id) from the current URL,
    // ignoring any /public/ prefix that may already be present to avoid stacking
    // /public/public/public/... on each redirect.
    const conversationMatch = window.location.pathname.match(/\/(c\/[^/?]+)/);
    const conversationSuffix = conversationMatch ? `/${conversationMatch[1]}` : '/';
    return `${environment.publicChatBaseUrl}${conversationSuffix}?${params}`;
  }

  private async checkAndInit(options: IClientOptionsForFrontend, parentOrigin: string | null = null) {
    const seq = ++this._initSequence;
    this._initOptions = options;
    this._initParentOrigin = parentOrigin;
    try {
      const optionsFromServer = await this.getOptionsWithAccessCheck(options);
      if (seq !== this._initSequence) {
        // A newer init superseded this run while its fetches were in flight
        // (postMessage options must beat the plain URL fallback regardless of
        // network timing) — drop the stale result instead of clobbering.
        return;
      }
      this.title.setTitle(optionsFromServer?.datasetName || 'OMM Contentbot');
      if (optionsFromServer && Object.keys(options).length === 1 && options.ds) {
        this.initViaOptions(optionsFromServer);
      } else {
        this.accessControlService.checkTrustedParentOrigin(parentOrigin);
        this.initViaOptions(options);
      }
    } catch (e) {
      this.loading = false;
      //this.error = true;
    }
  }

  /**
   * In cross-origin iframes, third-party cookie restrictions prevent the
   * browser from sending the oauth2-proxy session cookie. Use the Storage
   * Access API to request access to first-party cookies, then re-fetch the
   * user profile.
   *
   * When called without a prior user gesture (e.g. during init) the browser
   * may auto-grant access if the user recently visited the chatbot domain.
   * When called from a click handler (e.g. "Login" button) the gesture
   * satisfies the browser requirement.
   *
   * @returns true if the user was successfully authenticated after recovery.
   */
  private async tryRecoverSessionInIframe(): Promise<boolean> {
    if (typeof document.hasStorageAccess !== 'function' ||
        typeof document.requestStorageAccess !== 'function') {
      return false;
    }
    try {
      const hasAccess = await document.hasStorageAccess();
      if (!hasAccess) {
        await document.requestStorageAccess();
      }
      await this.authService.fetchProfile();
      return this.authService.isAuthenticated;
    } catch {
      return false;
    }
  }

  private initViaOptions(options: IClientOptionsForFrontend) {
    this.accessControlService.checkEmbeddingAccess(options);
    if (this.accessControlService.isBlocked) {
      this.stage = 'blocked';
      this.loading = false;
      return;
    }
    this.dataset = options.ds;
    this.subsetWeights = options.ssw || '';
    this.initialSubset = options.ss || '';
    this.detectFollowUp = options.fq != null ? options.fq !== 'false' : true; // default: true
    this.subset = {
      text: '',
      value: this.initialSubset,
      persona: options.persona,
    };
    this.welcomeMessage = options.welcomeMessage;
    this.sampleQuestions = options.sampleQuestions || [];
    if (this.sampleQuestions.length > 4) {
      // select random 4 questions
      this.sampleQuestions = this.sampleQuestions
        .sort(() => 0.5 - Math.random())
        .slice(0, 4);
    }
    this.initialQuestion = options.initialQuestion;
    this.inputPlaceholder = options.inputPlaceholder || 'Your question...';
    this.sendButtonLabel = options.sendButtonLabel || 'Send';
    this.assistant = options.assistant || { name: 'Assistant' };
    this.queryUserInfo = options.queryUserInfo;
    this.showNewConversationButton = options.showNewConversationButton ?? true;
    this.showSourcePills = options.showSourcePills ?? false;
    this.popoutEnabled = options.popout === true;
    this.showRating = options.showRating ?? true; // default to true for legacy compatibility, can be set to false via options for new embeds
    this.negativeRatingText = options.negativeRatingText || '';
    this.showEmailOnNegativeRating = options.showEmailOnNegativeRating ?? true;
    this.detailsButtons = options.detailsButtons || [];
    this.type = options.type;
    this.theme = options.theme || 'v1';

    // Handle body background class for v2 theme
    this.applyThemeToBody();

    if (options.cssUrl) {
      const link = this.document.createElement('link');
      link.setAttribute('rel', 'stylesheet');
      link.setAttribute('type', 'text/css');
      link.setAttribute('href', options.cssUrl);
      this.document.head.appendChild(link);
    }

    if (options.css) {
      const style = this.document.createElement('style');
      style.appendChild(this.document.createTextNode(options.css));
      this.document.head.appendChild(style);
    }

    this.loading = false;
  }

  private applyThemeToBody() {
    // Remove bg-slate-100 for v2 theme, keep it for v1 (backwards compatibility)
    if (this.theme === 'v2') {
      this.document.body.classList.remove('bg-slate-100');
      this.document.body.style.backgroundColor = '#ffffff';
    } else {
      // Ensure v1 has the class (in case it was removed)
      if (!this.document.body.classList.contains('bg-slate-100')) {
        this.document.body.classList.add('bg-slate-100');
      }
    }
  }

  ngOnInit() {
    this.scrollToBottom();
    // autofocus queryInput and auto-resize if needed
    setTimeout(() => {
      if (this.queryInput) {
        this.queryInput.nativeElement.focus();
        this.autoResize();
      }
    }, 100);

    // Add click listeners for source references
    this.setupSourceReferenceListeners();
  }

  /**
   * Get normalized files array from message entry.
   * Files can be in entry.files or entry.data.files depending on context.
   */
  public getFiles(entry: IMessage): Array<{ originalname: string; mimetype: string; size: number }> {
    return entry.files || entry.data?.files || [];
  }

  /**
   * Get the processed content for a message entry, stripping wrapping code fences
   * that some LLMs add around their entire markdown response.
   */
  public getProcessedContent(entry: IMessage): string {
    let content = (entry.content || '') + (entry.tempMarkdownFixContent || '');
    if (!entry.messageInProgress) {
      content = stripWrappingCodeFence(content);
    }
    return content;
  }

  /**
   * Set up click listeners for .cb-src spans (source references)
   * This allows users to click on [1], [2], etc. and scroll to the corresponding pill
   */
  private setupSourceReferenceListeners(): void {
    // Use event delegation on the chat entries container
    this.document.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if clicked element or its parent is a .cb-src span
      const sourceRef = target.closest('.cb-src');
      if (sourceRef) {
        event.preventDefault();
        const refNumber = sourceRef.getAttribute('data-ref');
        if (refNumber) {
          this.scrollToSourcePill(parseInt(refNumber, 10));
        }
      }
    });
  }

  /**
   * Scroll to and highlight a source pill by reference number
   */
  private scrollToSourcePill(refNumber: number): void {
    // Find all source pills and locate the one containing this reference
    const pills = this.document.querySelectorAll('.source-pill');

    for (const pill of Array.from(pills)) {
      const refs = pill.getAttribute('data-refs');
      if (refs) {
        const refNumbers = refs.split(',').map(r => parseInt(r.trim(), 10));
        if (refNumbers.includes(refNumber)) {
          pill.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          pill.classList.add('highlighted');
          setTimeout(() => {
            pill.classList.remove('highlighted');
          }, 2000);
          break;
        }
      }
    }
  }

  public selectSampleQuestion(question: string) {
    this.inputText = question;
    this.sendMessage();
  }

  public selectSubset(subset?: ISubset) {
    if (!subset) {
      this.subset = {
        text: '',
        value: '',
        functions: '',
      };
    } else {
      this.subset = subset;
    }
  }

  startNewConversation() {
    this.id = '';
    this.messages = [];
  }

  /**
   * Popped-out window init: wait for the opener's same-origin handshake to
   * authorize this top-level window (lifting the embedding guard the opener
   * already enforced), then load a fresh chat.
   */
  private async initPopout(): Promise<void> {
    this.loading = true;
    const auth = await this.popoutService.awaitAuthorization();
    if (auth) {
      this.accessControlService.authorizePopout();
    }
    const skipServerConfig = getQueryParam('skipServerConfig') === 'true';
    if (!skipServerConfig) {
      this.checkAndInit({ ds: this.dataset });
    } else {
      this.loading = false;
    }
  }

  /**
   * Open the chat in a separate window. Legacy v1 starts a fresh conversation
   * there (best-effort); the in-page view is frozen until the popout closes.
   */
  public openPopout(): void {
    this.popoutService.openPopout(this.dataset, this._initParentOrigin);
  }

  /**
   * Handle keydown events for the textarea
   * - Enter without Shift: send message
   * - Shift+Enter: insert new line (default behavior)
   */
  public handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /**
   * Handle paste events to auto-resize textarea when multiline content is pasted
   */
  public handlePaste(event: ClipboardEvent) {
    setTimeout(() => {
      this.autoResize();
    }, 0);
  }

  /**
   * Automatically resize textarea based on content
   */
  public autoResize() {
    if (!this.queryInput) return;

    const textarea = this.queryInput.nativeElement as HTMLTextAreaElement;

    // Reset height to recalculate
    textarea.style.height = 'auto';

    // Calculate new height (with min and max height limits based on the calculated CSS)
    const minHeight = parseInt(getComputedStyle(textarea).minHeight.replace('px', ''), 10) || 42;
    const maxHeight = parseInt(getComputedStyle(textarea).maxHeight.replace('px', ''), 10) || 200;
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));

    textarea.style.height = newHeight + 'px';

    // Add scrollbar if content exceeds max height
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  scrollToBottom(): void {
    try {
      setTimeout(() => {
        if (!this.chatEntriesContainer) return;
        this.chatEntriesContainer.nativeElement.scrollTop =
          this.chatEntriesContainer.nativeElement.scrollHeight;
      }, 50);
    } catch (err) {
    }
  }



  public sendMessage() {
    const inputText = this.inputText;
    this.inputText = '';

    // Reset textarea height after clearing text
    setTimeout(() => {
      this.autoResize();
    }, 0);

    if (!inputText) return;
    return this.sendMessageAction(inputText, this.subset);
  }

  public resendMessageWithSubset(subset: ISubset) {
    let lastUserMessage = null;
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].role === 'user') {
        lastUserMessage = this.messages[i];
        break;
      }
    }
    if (!lastUserMessage) return;

    return this.sendMessageAction(lastUserMessage.content, subset);
  }

  public async sendMessageAction(inputText: string, subset: ISubset) {
    if (this.botIsTyping) return;

    if (this.isAgentic) {
      this.queryAgent(inputText);
      return;
    }

    this.botIsTyping = true;

    const message: IMessage = {
      role: 'user',
      content: inputText,
    };

    try {
      const preparedMessageResponse = await fetch('/api/v1/prepare', {
        method: 'POST',
        body: JSON.stringify({ prompt: message.content, datasetId: this.dataset }),
        headers: {
          'Content-Type': 'application/json',
        },
      }).then((response) => response.json());

      if (preparedMessageResponse.prompt) {
        message.hint =
          'Der Inhalt der Frage war zu lang und wurde automatisch gekürzt.';
        message.content = preparedMessageResponse.prompt;
      }
    } catch (e) {
      console.error(e);
    }

    this.messages.push(message);
    // get query-param for "dataset"
    const newMessage: IMessage = {
      role: 'assistant',
      content: '',
      messageInProgress: true,
    };
    const self = this;
    this.scrollToBottom();

    const userInfo = this.queryUserInfo?.reduce((acc: any, field) => {
      acc[field.key] = field.value;
      return acc;
    }, {});

    const targetModel = getQueryParam('tg_m');
    // as fetch with ReadableStream, since http-client does not support it
    const response = await fetch('/api/v1/query', {
      method: 'POST',
      body: JSON.stringify({
        prompt: message.content,
        subset: subset.value || '',
        functions: subset.functions || '',
        persona: subset.persona,
        id: this.id,
        detect_fq: this.detectFollowUp,
        dataset: this.dataset,
        ssw: this.subsetWeights,
        tg_m: targetModel,
        userInfo
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Detect expired session — native fetch bypasses the Angular interceptor
    if (response.status === 401 && this.protectedWithLogin) {
      this.authService.login(window.location.href, false, this.kcIdpHint);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) return;

    let messageContentBuffer = '';
    let done = false;
    let typewriterInterval: any | null = null;

    async function* streamGenerator(reader: ReadableStreamDefaultReader<Uint8Array>) {
      while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      yield new TextDecoder('utf-8').decode(value);
      }
    }

    const stream = streamGenerator(reader);

    let chunkBuffer = '';
    for await (const chunk of stream) {
      chunkBuffer += chunk;
      const lines = chunkBuffer.split('\n');
      chunkBuffer = lines.pop() || '';
      for (const line of lines) {
      processChunk(line);
      }
    }
    if (chunkBuffer.trim()) {
      processChunk(chunkBuffer);
    }

    function processChunk(chunk: string) {
      let data: IQueryResponseUpdate;
      try {
        data = JSON.parse(chunk);
      } catch (e) {
        console.error(e);
        return;
      }

      // Only add the assistant message when we actually have content to show
      // (not for tool_usage events which are separate messages)
      if (!self.messages.includes(newMessage) && data.type === 'message') {
        self.messages.push(newMessage);
      }

      if (typewriterInterval) {
        clearInterval(typewriterInterval);
        typewriterInterval = null;
        newMessage.tempMarkdownFixContent = '';

        if (messageContentBuffer.length > 0) {
          newMessage.content += messageContentBuffer;
          messageContentBuffer = '';
          if (!done) {
          self.checkForUnfinishedMdLinks(newMessage);
          }
        }
      }
      if (data.type === 'message' && data.message?.length && !done) {
        messageContentBuffer += data.message;
        typewriterInterval = setInterval(() => {
          if (!done && messageContentBuffer) {
            newMessage.tempMarkdownFixContent = '';
            const firstThreeCharacters = messageContentBuffer.slice(0, 3);
            newMessage.content += firstThreeCharacters;
            messageContentBuffer = messageContentBuffer.slice(3);

            while (/[-#*] ?$/.test(newMessage.content)) {
              messageContentBuffer = newMessage.content.slice(-1) + messageContentBuffer;
              newMessage.content = newMessage.content.slice(0, -1);
            }

            self.checkForUnfinishedMdLinks(newMessage);
          }
        }, 20);

        self.scrollToBottom();
      } else if (data.type === 'tool_usage') {
        // Add tool usage as a small bubble in the timeline
        const toolUsageData = data as IQueryResponseToolUsageUpdate;
        const toolMessage: IMessage = {
          role: 'tool',
          agentName: toolUsageData.agentName,
          content: '', // No content needed, we'll use toolName
          toolName: toolUsageData.toolName,
          toolCallId: toolUsageData.toolCallId, // Store unique tool call ID
          timestamp: toolUsageData.timestamp,
          messageInProgress: false,
          toolStatus: 'running', // Mark as running initially
        };
        if (!self.messages.includes(toolMessage)) {
          self.messages.push(toolMessage);
        }
        // Still update lastToolUsage for the working indicator (fallback)
        self.lastToolUsage = {
          toolName: toolUsageData.toolName,
          agentName: toolUsageData.agentName,
        };
        self.scrollToBottom();
      } else if (data.type === 'tool_complete') {
        // Mark the specific tool as completed
        const toolCompleteData = data as IQueryResponseToolCompleteUpdate;
        const toolToComplete = self.messages.find((msg: IMessage) =>
          msg.role === 'tool' &&
          msg.toolCallId === toolCompleteData.toolCallId
        );
        if (toolToComplete) {
          toolToComplete.toolStatus = 'completed';
        }
        // Clear the working indicator for this specific tool
        if (self.lastToolUsage?.toolName === toolCompleteData.toolName) {
          self.lastToolUsage = undefined;
        }
        self.scrollToBottom();
      } else if (data.type === 'meta' && data.metaType === 'done') {
        done = true;
        newMessage.tempMarkdownFixContent = '';
        newMessage.messageInProgress = false;
        newMessage.content += messageContentBuffer;
        messageContentBuffer = '';
        if (typewriterInterval) {
          clearInterval(typewriterInterval);
          typewriterInterval = null;
        }

        newMessage.id = data.metadata.messageId;
        self.id = data.metadata.conversationId;

        // Mark any remaining running tools as completed (fallback, should not be needed)
        self.messages.forEach((msg: IMessage) => {
          if (msg.role === 'tool' && msg.toolStatus === 'running') {
            msg.toolStatus = 'completed';
          }
        });

        // Clear the working indicator (fallback)
        self.lastToolUsage = undefined;

        // Make sure the assistant message is added if it hasn't been yet
        if (!self.messages.includes(newMessage)) {
          self.messages.push(newMessage);
        }
      } else if (data.type === 'meta' && data.metaType === 'chosen_contexts') {
        newMessage.context = data.metadata.chosenContexts;
        const subsetReferences = (data.metadata.chosenContexts || [])
          .map((context: any) => context.subsetReferences)
          .flat()
          .filter((r: any) => !!r)
          .map((ref: any) => ({
            text: ref.label || ref.tag,
            value: '[TAG]::' + ref.tag,
          }));
        const subsetReferencesMap: any = {};
        subsetReferences.forEach((ref: any) => {
        subsetReferencesMap[ref.value] = ref;
        });
        newMessage.subsetReferences = Object.values(subsetReferencesMap);
        self.scrollToBottom();
      } else if (data.type === 'meta' && data.metaType === 'error') {
        const errorText = data.metadata.errors.map((error: string) => ERROR_MAP[error] || error).join('\n');
        // cc-qx52: never discard an answer the user already watched arrive.
        // This widget has no separate error chip, so the reason is appended
        // below the preserved text instead of replacing it.
        newMessage.content = newMessage.content?.trim()
          ? `${newMessage.content}\n\n${errorText}`
          : errorText;
        newMessage.messageInProgress = false;
        // Make sure the error message is added
        if (!self.messages.includes(newMessage)) {
          self.messages.push(newMessage);
        }
      }
    }

    // Final check: ensure the assistant message is in the list
    // (in case we only received tool_usage and meta events, no actual message content)
    if (!this.messages.includes(newMessage) && (newMessage.content || newMessage.id)) {
      this.messages.push(newMessage);
    }

    this.botIsTyping = false;
  }

  public async queryAgent(inputText: string) {
    if (this.botIsTyping) return;
    this.botIsTyping = true;
    this.messages.push({
      role: 'user',
      content: inputText,
    });
    this.scrollToBottom();

    // multipart form upload to `/api/v1/agent/${this.dataset}/query`
    const formData = new FormData();
    formData.append('prompt', inputText);
    formData.append('datasetId', this.dataset);
    formData.append('conversationId', this.id || '');

    // append all files als "files" to the formData
    this.pickedFiles.forEach(file => {
      formData.append('files', file);
    });

    this.pickedFiles = [];
    const response = await fetch(`/api/v1/agent/${this.dataset}/query`, {
      method: 'POST',
      body: formData,
    });

    // Detect expired session — native fetch bypasses the Angular interceptor
    if (response.status === 401 && this.protectedWithLogin) {
      this.authService.login(window.location.href, false, this.kcIdpHint);
      return;
    }

    if (!response.body) {
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();

      if (value) {
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        lines.forEach(line => {
          if (line) {
            try {
              const data = JSON.parse(line);

              if (data.type === 'message' && data.message) {
                if (data.conversationId) {
                  this.id = data.conversationId;
                }
                // Mark any remaining running tools as completed (fallback)
                this.messages.forEach((msg: IMessage) => {
                  if (msg.role === 'tool' && msg.toolStatus === 'running') {
                    msg.toolStatus = 'completed';
                  }
                });
                this.lastToolUsage = undefined;

                this.messages.push({
                  role: 'assistant',
                  agentName: data.agentName,
                  content: data.message,
                  messageInProgress: false,
                });
              } else if (data.type === 'tool_usage') {
                // Add tool usage as a small bubble in the timeline
                const toolUsageData = data as IQueryResponseToolUsageUpdate;
                this.messages.push({
                  role: 'tool',
                  agentName: toolUsageData.agentName,
                  content: '', // No content needed, we'll use toolName
                  toolName: toolUsageData.toolName,
                  toolCallId: toolUsageData.toolCallId, // Store unique tool call ID
                  timestamp: toolUsageData.timestamp,
                  messageInProgress: false,
                  toolStatus: 'running', // Mark as running initially
                });
                // Still update lastToolUsage for the working indicator (fallback)
                this.lastToolUsage = {
                  toolName: toolUsageData.toolName,
                  agentName: toolUsageData.agentName,
                };
              } else if (data.type === 'tool_complete') {
                // Mark the specific tool as completed
                const toolCompleteData = data as IQueryResponseToolCompleteUpdate;
                const toolToComplete = this.messages.find((msg: IMessage) =>
                  msg.role === 'tool' &&
                  msg.toolCallId === toolCompleteData.toolCallId
                );
                if (toolToComplete) {
                  toolToComplete.toolStatus = 'completed';
                }
                // Clear the working indicator for this specific tool
                if (this.lastToolUsage?.toolName === toolCompleteData.toolName) {
                  this.lastToolUsage = undefined;
                }
              }
              this.scrollToBottom();
            } catch (e) {
              console.log('Empfangener Text:', line);
            }
          }
        });
      }

      if (done) {
        break;
      }

    }

    this.lastToolUsage = undefined;
    this.botIsTyping = false;
  }

  public onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        const file = input.files[i];
        if (!this.pickedFiles.some((f) => f.name === file.name)) {
          this.pickedFiles.push(file);
        }
      }
      input.value = '';
    }
  }

  public removeFile(file: File) {
    const index = this.pickedFiles.indexOf(file);
    if (index > -1) {
      this.pickedFiles.splice(index, 1);
    }
  }

  private checkForUnfinishedMdLinks(message: IMessage) {
    // check if the messageContent has an "open link" at the end, e.g. "... [mylink" or "... [mylink]" or "... [mylink](htt"
    // if so, temporarily "finish" the messageContent by adding e.g. "]()" or "()" or ")" to "newMessage.tempMarkdownFixContent"
    if (UNFINISHED_MARKDOWN_LINK_REGEX.test(message.content)) {
      UNFINISHED_MARKDOWN_LINK_REGEX.lastIndex = 0;
      const lastBracketMatch = UNFINISHED_MARKDOWN_LINK_REGEX.exec(message.content);
      if (lastBracketMatch && lastBracketMatch[0]) {
        const lastBracketMatchContent = lastBracketMatch[0];
        if (!lastBracketMatchContent.includes('](')) {
          message.tempMarkdownFixContent = ']()';
        } else if (!lastBracketMatchContent.includes('(')) {
          message.tempMarkdownFixContent = '()';
        } else if (!lastBracketMatchContent.includes(')')) {
          message.tempMarkdownFixContent = ')';
        }
      }
    }
  }

  async startLogin() {
    if (this.isInIframe) {
      await this.handleIframeAuth(() => this.authService.loginPopup(true, this.kcIdpHint));
      return;
    }
    this.authService.login(window.location.href, true, this.kcIdpHint);
  }

  async startRegister() {
    if (this.isInIframe) {
      await this.handleIframeAuth(() => this.authService.registerPopup(this.kcIdpHint));
      return;
    }
    this.authService.register(window.location.href);
  }

  private async handleIframeAuth(openPopup: () => Promise<boolean>) {
    // First, try to recover an existing session via the Storage Access API.
    // This can work if the user previously visited the chatbot domain directly
    // and the browser auto-grants cookie access within the user gesture.
    const recovered = await this.tryRecoverSessionInIframe();
    if (recovered && this._initOptions) {
      this.stage = 'chat';
      this.loading = true;
      await this.checkAndInit(this._initOptions, this._initParentOrigin);
      return;
    }

    // Open the popup for login/registration. The popup runs in a first-party
    // context where it can set session cookies and fetch the user profile.
    // The profile is relayed back to us via postMessage to bypass cookie
    // partitioning in the iframe.
    const popupSuccess = await openPopup();
    if (popupSuccess && this._initOptions) {
      this.stage = 'chat';
      this.loading = true;
      await this.checkAndInit(this._initOptions, this._initParentOrigin);
    }
  }

  logoutUser() {
    this.authService.logout();
  }

  ngOnDestroy() {
    if (this._urlInitFallbackTimer !== null) {
      clearTimeout(this._urlInitFallbackTimer);
      this._urlInitFallbackTimer = null;
    }
    // Restore bg-slate-100 class when component is destroyed (if we removed it)
    if (this.theme === 'v2') {
      this.document.body.classList.add('bg-slate-100');
      this.document.body.style.backgroundColor = '';
    }
  }
}

function getQueryParams() {
  const params: any = {};
  const query = window.location.search.substring(1);
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split('=');
    params[pair[0]] = decodeURIComponent(pair[1]);
  }
  return params;
}

function getQueryParam(paramName: string): string {
  const params = getQueryParams();
  return params[paramName];
}

function getQueryParamJson(paramName: string): any {
  let param = getQueryParam(paramName);
  const isBase64 =
    /^([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==)?$/.test(param);
  if (isBase64) {
    param = atob(param);
  }
  if (!param) return null;
  try {
    return JSON.parse(param);
  } catch (e) {
    return null;
  }
}
